// Chat top-level que vive al inicio de /monitoring/hcd.
// El agente IA tiene acceso a:
//   - Snapshot completo del HCD (autoridades, comisiones, digesto, actas,
//     expedientes, prensa, historia) — vía /api/messaging/ask-hcd.
//   - Índice RAG de 27k chunks (FTS + embeddings) — incluye PDFs reales
//     de ordenanzas, decretos, resoluciones, comunicaciones y actas.
//
// Las citas [N] (digesto) y [RAG-N] (chunks indexados) son chips
// clickeables que abren popup con el detalle de la fuente.

import React, { useEffect, useRef, useState } from 'react';
import friendlyError from '../../utils/friendlyError';
import styled, { keyframes } from 'styled-components';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  FaPaperPlane,
  FaRobot,
  FaUser,
  FaTrashAlt,
  FaLandmark,
  FaExclamationCircle,
} from 'react-icons/fa';
import {
  borderSoft,
  panelDeep,
  mono,
  accent,
  ErrorBox,
} from '../../styles/techComponents';
import { askHcd } from '../../services/monitoringService';
import { CitePopup, wrapWithCitations } from '../UI/Citations';

const dotBounce = keyframes`
  0%, 60%, 100% { transform: translateY(0); opacity: 0.35; }
  30%           { transform: translateY(-4px); opacity: 1; }
`;

const ChatWrap = styled.section`
  background: ${panelDeep};
  border: 1px solid rgba(0, 180, 216, 0.18);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin-bottom: 1.25rem;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.18), 0 0 0 1px rgba(0, 180, 216, 0.06);
`;

const ChatHeader = styled.div`
  padding: 0.9rem 1.1rem;
  border-bottom: 1px solid ${borderSoft};
  display: flex;
  align-items: center;
  gap: 0.7rem;
  background:
    linear-gradient(180deg, rgba(0, 180, 216, 0.07), transparent);

  .icon {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: rgba(0, 180, 216, 0.15);
    border: 1px solid rgba(0, 180, 216, 0.35);
    color: ${accent};
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .title {
    flex: 1;
    color: var(--text-primary);
    font-weight: 600;
    font-size: 0.98rem;
  }
  .sub {
    font-family: ${mono};
    font-size: 0.6rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--text-disabled);
  }
  .clear {
    background: transparent;
    border: 1px solid ${borderSoft};
    color: var(--text-disabled);
    padding: 5px 10px;
    border-radius: 6px;
    font-size: 0.74rem;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    &:hover { color: var(--alert-error); border-color: rgba(239, 71, 111, 0.4); }
  }
`;

const ChatBody = styled.div`
  padding: 1rem 1.1rem;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  max-height: 380px;
  overflow-y: auto;
  background: rgba(11, 19, 32, 0.25);

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.08);
    border-radius: 6px;
  }
`;

const Hint = styled.div`
  color: var(--text-disabled);
  font-size: 0.85rem;
  line-height: 1.5;
  padding: 0.6rem 0.4rem;

  .pills {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
    margin-top: 0.75rem;
  }
`;

const SuggestPill = styled.button`
  background: transparent;
  border: 1px solid ${borderSoft};
  color: var(--text-secondary);
  font-size: 0.78rem;
  padding: 0.38rem 0.75rem;
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.18s ease;
  &:hover { color: ${accent}; border-color: rgba(0, 180, 216, 0.45); background: rgba(0, 180, 216, 0.05); }
`;

const Msg = styled.div`
  display: flex;
  gap: 0.7rem;
  align-items: flex-start;

  .avatar {
    flex-shrink: 0;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid ${borderSoft};
    background: ${({ $role }) => ($role === 'user' ? 'rgba(0, 180, 216, 0.08)' : 'rgba(6, 214, 160, 0.08)')};
    color: ${({ $role }) => ($role === 'user' ? accent : 'var(--alert-success)')};
    font-size: 0.78rem;
  }
  .bubble {
    flex: 1;
    min-width: 0;
    background: rgba(11, 19, 32, 0.55);
    border: 1px solid ${borderSoft};
    border-radius: 8px;
    padding: 0.7rem 0.9rem;
    color: var(--text-secondary);
    font-size: 0.9rem;
    line-height: 1.55;
    p { margin: 0 0 0.5rem; }
    p:last-child { margin: 0; }
    ul, ol { margin: 0.4rem 0 0.4rem 1.2rem; padding: 0; }
    h1, h2, h3, h4 { color: var(--text-primary); margin: 0.6rem 0 0.35rem; font-size: 0.96rem; }
    table { border-collapse: collapse; margin: 0.5rem 0; font-size: 0.84rem; }
    th, td { border: 1px solid ${borderSoft}; padding: 4px 8px; }
    th { color: var(--text-primary); background: rgba(0, 180, 216, 0.06); }
    blockquote { border-left: 2px solid ${accent}; padding-left: 0.7rem; margin: 0.4rem 0; color: var(--text-disabled); }
    a { color: ${accent}; }
    code { font-family: ${mono}; background: rgba(0, 180, 216, 0.1); color: ${accent}; padding: 1px 5px; border-radius: 3px; font-size: 0.84rem; }
  }
  .meta {
    margin-top: 4px;
    font-family: ${mono};
    font-size: 0.6rem;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--text-disabled);
  }
`;

const Typing = styled.div`
  display: inline-flex;
  gap: 4px;
  align-items: center;
  span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${accent};
    animation: ${dotBounce} 1.1s infinite;
    &:nth-child(2) { animation-delay: 0.15s; }
    &:nth-child(3) { animation-delay: 0.3s; }
  }
`;

const InputWrap = styled.form`
  border-top: 1px solid ${borderSoft};
  padding: 0.75rem 0.9rem;
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
  background: rgba(11, 19, 32, 0.4);
`;

const ChatInput = styled.textarea`
  flex: 1;
  resize: none;
  min-height: 40px;
  max-height: 130px;
  background: rgba(11, 19, 32, 0.65);
  border: 1px solid ${borderSoft};
  border-radius: 7px;
  color: var(--text-primary);
  font-family: inherit;
  font-size: 0.88rem;
  padding: 0.55rem 0.75rem;
  outline: none;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
  &::placeholder { color: var(--text-disabled); }
  &:focus {
    border-color: rgba(0, 180, 216, 0.5);
    box-shadow: 0 0 0 2px rgba(0, 180, 216, 0.12);
  }
`;

const SendBtn = styled.button`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: 1px solid rgba(0, 180, 216, 0.35);
  background: rgba(0, 180, 216, 0.15);
  color: ${accent};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.18s ease;
  &:hover:not(:disabled) { background: rgba(0, 180, 216, 0.3); transform: translateY(-1px); }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

const SUGGESTIONS = [
  '¿Qué ordenanzas se aprobaron sobre humedales o medio ambiente?',
  'Mostrame los expedientes recientes sobre el puerto',
  '¿Cómo se compone la Comisión de Presupuesto y Hacienda?',
  '¿Qué dice la última acta de sesiones?',
  'Listame las multas o sanciones administrativas que aparecen en el digesto',
  'Resumime la historia del HCD San Nicolás',
];

const STORAGE_KEY = 'arcana_hcd_chat_v1';

export default function HcdChat() {
  const [messages, setMessages] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [input, setInput] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);
  const [activeCite, setActiveCite] = useState(null);
  const [activeRag, setActiveRag] = useState([]);
  const bodyRef = useRef(null);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(messages.slice(-30))); } catch {}
  }, [messages]);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, pending]);

  const openCite = (cite, rag) => {
    setActiveRag(rag || []);
    setActiveCite(cite);
  };

  const send = async (override) => {
    const q = (override ?? input).trim();
    if (!q || pending) return;
    setError(null);
    const userMsg = { role: 'user', content: q, ts: Date.now() };
    setMessages((m) => [...m, userMsg]);
    setInput('');
    setPending(true);
    try {
      const res = await askHcd(q);
      setMessages((m) => [
        ...m,
        {
          role: 'assistant',
          content: res.answer || '(respuesta vacía)',
          ts: Date.now(),
          summary: res.context_summary,
          sources_rag: res.sources_rag || [],
        },
      ]);
    } catch (e) {
      setError(friendlyError(e, 'Error en la consulta'));
    } finally {
      setPending(false);
    }
  };

  const clear = () => {
    setMessages([]);
    setError(null);
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <ChatWrap>
      <ChatHeader>
        <span className="icon"><FaLandmark /></span>
        <span className="title">Asistente HCD San Nicolás</span>
        <span className="sub">RAG · 27k chunks</span>
        {messages.length > 0 && (
          <button type="button" className="clear" onClick={clear}>
            <FaTrashAlt /> Limpiar
          </button>
        )}
      </ChatHeader>
      <ChatBody ref={bodyRef}>
        {messages.length === 0 && !pending && (
          <Hint>
            Preguntale sobre cualquier expediente, ordenanza, comisión, concejal o nota de prensa.
            El agente tiene acceso al snapshot completo del HCD + 27.039 chunks indexados
            (FTS + embeddings semánticos sobre todos los PDFs reales).
            <div className="pills">
              {SUGGESTIONS.map((s) => (
                <SuggestPill key={s} type="button" onClick={() => send(s)}>{s}</SuggestPill>
              ))}
            </div>
          </Hint>
        )}
        {messages.map((m, i) => {
          const handleCite = (cite) => openCite(cite, m.sources_rag);
          return (
            <Msg key={i} $role={m.role}>
              <div className="avatar">{m.role === 'user' ? <FaUser /> : <FaRobot />}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="bubble">
                  {m.role === 'assistant' ? (
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={wrapWithCitations(handleCite)}
                    >
                      {m.content}
                    </ReactMarkdown>
                  ) : (
                    m.content.split('\n').map((line, k) => <p key={k}>{line}</p>)
                  )}
                </div>
                {m.summary && (
                  <div className="meta">
                    {m.summary.rag_chunks ? `${m.summary.rag_chunks} RAG · ` : ''}
                    {m.sources_rag?.length ? 'click en ◇N para ver fuente' : ''}
                  </div>
                )}
              </div>
            </Msg>
          );
        })}
        {pending && (
          <Msg $role="assistant">
            <div className="avatar"><FaRobot /></div>
            <div className="bubble"><Typing><span /><span /><span /></Typing></div>
          </Msg>
        )}
        {error && (
          <ErrorBox style={{ margin: 0 }}>
            <FaExclamationCircle style={{ marginRight: 6 }} /> {error}
          </ErrorBox>
        )}
      </ChatBody>
      <InputWrap onSubmit={(e) => { e.preventDefault(); send(); }}>
        <ChatInput
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Preguntale al asistente sobre el HCD…  (Enter envía · Shift+Enter salto de línea)"
          disabled={pending}
          rows={1}
        />
        <SendBtn type="submit" disabled={pending || !input.trim()} aria-label="Enviar">
          <FaPaperPlane />
        </SendBtn>
      </InputWrap>
      {activeCite && (
        <CitePopup
          cite={activeCite}
          indexed={[]}
          fresh={[]}
          rag={activeRag}
          onClose={() => setActiveCite(null)}
        />
      )}
    </ChatWrap>
  );
}
